import {
  AnimatedCollapsible,
  EmptyState,
  LoadingState,
  Modal,
} from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import {
  selectCurrentUser,
  selectHasAccessControl,
} from '@store/auth/authSelectors';
import { selectAllOutlets } from '@store/outlets';
import { FileDown, Mail, Printer, Send } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  emailBill,
  exportBillPdf,
} from '../../../services/billDocumentService';
import { sendCreditSettlementEmail } from '../../../services/orderService';
import {
  buildBillItems,
  resolveBillClientDetails,
  resolveBillPrinter,
} from '../../../services/billPrintingService';
import { printBillTicket } from '../../../services/printerService';
import { fetchReportOrderDetails } from '../../../services/reportsService';
import type { SalesReportOrderDetails } from '../../../types/report';
import type {
  OrderDetailsMetaRowProps,
  OrderDetailsReceiptHeaderProps,
  OrderDetailsReceiptItemsTableProps,
  OrderDetailsReceiptTotalsProps,
  OrderDetailsSectionProps,
  ReportOrderDetailsModalProps,
} from '../../../types/reports';
import { apiError, apiSuccess } from '../../../util/notify';
import {
  formatReportDate,
  formatReportMode,
  formatReportMoney,
  formatReportUserName,
} from '../../../util/reporting';
import { BillActionButton } from './BillActionButton';
import { BillEmailPanel } from './BillEmailPanel';

const DETAIL_DATE_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const parseDetailTimestamp = (value: string): Date | null => {
  const normalized = value.replace(/(\.\d{3})\d+([+-]\d{2}:\d{2}|Z)$/, '$1$2');
  const parsed = new Date(normalized);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return null;
};

const buildOrderHeading = (details: SalesReportOrderDetails): string => {
  const { order } = details;

  if (order.table_name) return `${order.table_name} #${order.bill_number}`;
  if (order.client_name) return `${order.client_name} #${order.bill_number}`;

  return `${formatReportMode(order.mode)} #${order.bill_number}`;
};

const formatClock = (value: string): string => {
  const parsed = parseDetailTimestamp(value);
  if (!parsed) return '—';

  return parsed.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDetailDate = (value: string): string => {
  const parsed = parseDetailTimestamp(value);
  if (!parsed) return value;

  return DETAIL_DATE_FORMATTER.format(parsed);
};

const buildPrintedByName = (
  user: ReturnType<typeof selectCurrentUser>,
): string => {
  if (!user) return 'System';

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.email || 'System';
};

const buildServerName = (details: SalesReportOrderDetails): string => {
  const waiterName = details.order.waiter_name?.trim();
  if (waiterName) return waiterName;

  return formatReportUserName(
    details.order.created_by.first_name,
    details.order.created_by.last_name,
  );
};

const OrderDetailsSection: React.FC<OrderDetailsSectionProps> = ({
  title,
  subtitle,
  children,
}) => (
  <section className="">
    <div className="mb-3">
      <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-700">
        {title}
      </h4>
      {subtitle ? (
        <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
      ) : null}
    </div>
    {children}
  </section>
);

const OrderDetailsMetaRow: React.FC<OrderDetailsMetaRowProps> = ({
  label,
  value,
}) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="font-semibold uppercase text-slate-800">{label}:</span>
    <span className="text-slate-700">{value}</span>
  </div>
);

const OrderDetailsReceiptHeader: React.FC<OrderDetailsReceiptHeaderProps> = ({
  companyLabel,
  outletLabel,
  invoiceLabel,
  heading,
}) => (
  <div className="border-b border-slate-300 px-0 pb-4 text-center">
    <p className="mt-3 text-sm font-semibold text-slate-900">{companyLabel}</p>
    <p className="mt-1 text-sm text-slate-700">{outletLabel}</p>
    <p className="mt-2 text-sm font-semibold uppercase tracking-[0.12em] text-slate-800">
      {invoiceLabel}
    </p>
    <p className="mt-2 text-base font-semibold text-slate-900">{heading}</p>
  </div>
);

const OrderDetailsReceiptItemsTable: React.FC<
  OrderDetailsReceiptItemsTableProps
> = ({ items }) => (
  <div className="border-y border-slate-300">
    <div className="grid grid-cols-[minmax(0,1fr)_7rem] border-b border-slate-300 px-0 py-2 text-sm font-medium text-slate-800">
      <span>ITEM</span>
      <span className="text-right">Amount</span>
    </div>
    {items.length > 0 ? (
      items.map((item) => (
        <div
          key={`${item.name}-${item.quantity}-${item.amount}`}
          className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3 border-b border-slate-200 px-0 py-2 text-sm last:border-b-0"
        >
          <span className="text-slate-800">
            {item.quantity.toLocaleString()} {item.name}
          </span>
          <span className="text-right text-slate-800">
            {formatReportMoney(item.amount)}
          </span>
        </div>
      ))
    ) : (
      <div className="px-0 py-6 text-center text-sm text-slate-500">
        No billable items were found on this order.
      </div>
    )}
  </div>
);

const OrderDetailsReceiptTotals: React.FC<OrderDetailsReceiptTotalsProps> = ({
  totalAmount,
  discountAmount,
  netAmount,
}) => (
  <div className="space-y-1 border-b border-slate-300 py-3 text-sm">
    <div className="flex items-center justify-between font-semibold text-slate-900">
      <span>TOTAL</span>
      <span>{formatReportMoney(totalAmount)}</span>
    </div>
    {discountAmount > 0 ? (
      <div className="flex items-center justify-between text-slate-700">
        <span>DISCOUNT</span>
        <span>{formatReportMoney(discountAmount)}</span>
      </div>
    ) : null}
    <div className="flex items-center justify-between text-base font-bold text-slate-900">
      <span>BILL TOTAL</span>
      <span>{formatReportMoney(netAmount)}</span>
    </div>
  </div>
);

export const OrderDetailsModal: React.FC<ReportOrderDetailsModalProps> = ({
  isOpen,
  orderId,
  onClose,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const canReprintBills = useAppSelector(
    selectHasAccessControl(AccessControlCode.SALES_BILLS_REPRINT),
  );
  const allOutlets = useAppSelector(selectAllOutlets);

  const [details, setDetails] = useState<SalesReportOrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isEmailPanelOpen, setIsEmailPanelOpen] = useState(false);
  const [emailPanelKey, setEmailPanelKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsEmailPanelOpen(false);
  }, [isOpen, orderId]);

  useEffect(() => {
    let isActive = true;

    const loadDetails = async () => {
      if (!isOpen || !orderId) {
        setDetails(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const response = await fetchReportOrderDetails(orderId);
      if (!isActive) return;

      if (!response.success || !response.data) {
        setDetails(null);
        setError(response.error || 'Failed to load order details.');
        setIsLoading(false);
        return;
      }

      setDetails(response.data.data);
      setIsLoading(false);
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [isOpen, orderId]);

  const orderOutlet = useMemo(() => {
    if (!details) return null;

    return (
      allOutlets.find((outlet) => outlet.id === details.order.outlet_id) ||
      (currentUser?.outlet?.id === details.order.outlet_id
        ? currentUser.outlet
        : null)
    );
  }, [allOutlets, currentUser?.outlet, details]);

  const billItems = useMemo(
    () => (details ? buildBillItems(details.items) : []),
    [details],
  );

  const companyLabel =
    currentUser?.company?.business_name ||
    details?.order.outlet_name ||
    'Business';
  const billHeading = details ? buildOrderHeading(details) : '';
  const serverName = details ? buildServerName(details) : 'Not assigned';
  const printedByName = buildPrintedByName(currentUser);
  const orderDate = details?.order.created_at || details?.order.sale_date || '';
  const orderScopeLabel = details
    ? details.order.table_name || formatReportMode(details.order.mode)
    : '';

  const handlePrintBill = useCallback(async () => {
    if (!canReprintBills) {
      apiError('Your account does not have permission to re-print bills.');
      return;
    }

    if (!details) {
      apiError('Load the order details before re-printing the bill.');
      return;
    }

    if (billItems.length === 0) {
      apiError('This order has no billable items.');
      return;
    }

    setIsPrinting(true);

    try {
      const [printer, client] = await Promise.all([
        resolveBillPrinter(details),
        resolveBillClientDetails(details),
      ]);

      const isTakeAwayLikeOrder =
        details.order.mode !== 'DINE' || !details.order.section_id;

      const response = await printBillTicket({
        printer: {
          name: printer.name,
          connection_type: printer.connection_type,
          printer_identifier: printer.printer_identifier,
          ip_address: printer.ip_address,
          port: printer.port,
          paper_size: printer.paper_size,
          details: printer.details,
        },
        payload: {
          outlet_name: details.order.outlet_name,
          outlet_address: orderOutlet?.address ?? null,
          outlet_tin: orderOutlet?.tin_number ?? null,
          outlet_contact: orderOutlet?.phone ?? null,
          bill_number: details.order.bill_number,
          table_name:
            details.order.table_name || (isTakeAwayLikeOrder ? 'ORDER' : '-'),
          section_name:
            details.order.section_name ||
            (isTakeAwayLikeOrder ? 'TAKEAWAY' : '-'),
          waiter_name: details.order.waiter_name || '-',
          printed_by: printedByName,
          printed_at: new Date().toISOString(),
          is_reprint: true,
          items: billItems,
          client,
          total_amount: details.order.total_amount,
          discount_amount: details.order.discount_amount,
          net_amount: details.order.net_amount,
          outstanding_balance:
            typeof details.order.balance_amount === 'number'
              ? details.order.balance_amount
              : null,
          balance_brought_forward:
            typeof details.order.settlement_total === 'number'
              ? details.order.net_amount - details.order.settlement_total
              : null,
          footer_lines: [
            ...(orderOutlet?.receipt_footnote
              ? [orderOutlet.receipt_footnote]
              : []),
            'Powered by smartPOS Cloud',
            'www.smartposcloud.app',
          ],
        },
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to re-print bill');
      }

      apiSuccess(`Bill #${details.order.bill_number} sent to ${printer.name}`);
    } catch (printError) {
      apiError(
        printError instanceof Error
          ? printError.message
          : 'Failed to re-print bill',
      );
    } finally {
      setIsPrinting(false);
    }
  }, [
    billItems,
    canReprintBills,
    details,
    orderOutlet?.address,
    orderOutlet?.phone,
    orderOutlet?.receipt_footnote,
    orderOutlet?.tin_number,
    printedByName,
  ]);

  const handleSendCreditEmail = useCallback(async () => {
    if (!details || !orderId) return;
    setIsSendingEmail(true);
    try {
      await sendCreditSettlementEmail(orderId, details.order.outlet_id);
      apiSuccess('Credit bill email sent to client');
    } catch {
      apiError('Failed to send credit bill email');
    } finally {
      setIsSendingEmail(false);
    }
  }, [details, orderId]);

  const handleExportPdf = useCallback(async () => {
    if (!details || !orderId) return;
    setIsExportingPdf(true);
    const response = await exportBillPdf(orderId);
    setIsExportingPdf(false);

    if (!response.success) {
      apiError(response.error || 'Failed to generate the bill PDF');
      return;
    }
    apiSuccess(`Bill #${details.order.bill_number} downloaded`);
  }, [details, orderId]);

  const handleToggleEmailPanel = useCallback(() => {
    setIsEmailPanelOpen((open) => {
      if (!open) setEmailPanelKey((key) => key + 1);
      return !open;
    });
  }, []);

  const handleEmailBill = useCallback(
    async (recipients: string[]): Promise<string | null> => {
      if (!details || !orderId) return 'Load the bill before emailing it.';
      const response = await emailBill(orderId, recipients);
      if (!response.success) {
        return response.error || 'Failed to email the bill';
      }
      apiSuccess(
        `Bill #${details.order.bill_number} emailed to ${recipients.join(', ')}`,
      );
      setIsEmailPanelOpen(false);
      return null;
    },
    [details, orderId],
  );

  const isCreditBillWithClient =
    details?.order.status_name === 'CREDIT' &&
    Boolean(details?.order.client_id);

  const isBillUnavailable = isLoading || !details;
  const billActionBlockedReason = !canReprintBills
    ? 'Your account does not have permission to re-print, export or email bills'
    : billItems.length === 0 && details
      ? 'This order has no billable items'
      : undefined;
  const areBillActionsDisabled =
    isBillUnavailable || Boolean(billActionBlockedReason);

  const headerActions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {isCreditBillWithClient && (
        <BillActionButton
          icon={Send}
          label="Credit statement"
          onClick={handleSendCreditEmail}
          disabled={isBillUnavailable}
          isBusy={isSendingEmail}
          title="Email the credit statement and outstanding balance to the client"
        />
      )}
      <BillActionButton
        icon={Printer}
        label="Print"
        onClick={handlePrintBill}
        disabled={areBillActionsDisabled}
        isBusy={isPrinting}
        title={billActionBlockedReason || 'Re-print this bill'}
      />
      <BillActionButton
        icon={FileDown}
        label="PDF"
        onClick={handleExportPdf}
        disabled={areBillActionsDisabled}
        isBusy={isExportingPdf}
        title={billActionBlockedReason || 'Download this bill as a PDF'}
      />
      <BillActionButton
        icon={Mail}
        label="Email"
        onClick={handleToggleEmailPanel}
        disabled={areBillActionsDisabled}
        isActive={isEmailPanelOpen}
        title={billActionBlockedReason || 'Email this bill as a PDF'}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bill"
      size="lg"
      headerActions={headerActions}
    >
      {isLoading ? (
        <LoadingState message="Loading order details..." size="lg" />
      ) : error ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
          <EmptyState
            title="Order details unavailable"
            description="This order could not be loaded for the current company or outlet scope."
          />
        </div>
      ) : !details ? (
        <EmptyState
          title="No order selected"
          description="Choose an order from the report table to view its items, settlements, and applied discounts."
        />
      ) : (
        <div className="space-y-5 text-sm text-slate-800">
          <AnimatedCollapsible
            isOpen={isEmailPanelOpen}
            className="motion-reduce:transition-none"
          >
            <BillEmailPanel
              key={emailPanelKey}
              billNumber={details.order.bill_number}
              defaultRecipient={currentUser?.email || ''}
              onSend={handleEmailBill}
              onCancel={() => setIsEmailPanelOpen(false)}
            />
          </AnimatedCollapsible>
          <div className="mx-auto max-w-[34rem] space-y-4">
            <section className="bg-white py-5">
              <OrderDetailsReceiptHeader
                companyLabel={companyLabel}
                outletLabel={
                  orderOutlet?.address
                    ? `${details.order.outlet_name} · ${orderOutlet.address}`
                    : details.order.outlet_name
                }
                invoiceLabel={`Tax invoice ${details.order.bill_number}`}
                heading={billHeading}
              />

              <div className="space-y-2 py-4">
                <OrderDetailsMetaRow label="Table" value={orderScopeLabel} />
                <OrderDetailsMetaRow label="Served by" value={serverName} />
                <OrderDetailsMetaRow
                  label="Date"
                  value={`${formatReportDate(details.order.sale_date)} | ${formatClock(orderDate)}`}
                />
                <OrderDetailsMetaRow label="Printed by" value={printedByName} />
                {details.order.client_name ? (
                  <OrderDetailsMetaRow
                    label="Client"
                    value={details.order.client_name}
                  />
                ) : null}
              </div>

              <OrderDetailsReceiptItemsTable items={billItems} />

              <OrderDetailsReceiptTotals
                totalAmount={details.order.total_amount}
                discountAmount={details.order.discount_amount}
                netAmount={details.order.net_amount}
              />
            </section>

            <OrderDetailsSection
              title="Settlements"
              subtitle={`${details.settlements.length.toLocaleString()} settlement entries linked to this bill.`}
            >
              {details.settlements.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                  No settlements have been recorded for this order yet.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="max-h-[15rem] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-app-bg">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                          <th className="px-3 py-2">Time</th>
                          <th className="px-3 py-2">Method</th>
                          <th className="px-3 py-2">Settled By</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.settlements.map((settlement) => (
                          <tr
                            key={settlement.id}
                            className="border-t border-slate-100 align-top"
                          >
                            <td className="px-3 py-2 text-slate-700">
                              {formatClock(settlement.created_at)}
                            </td>
                            <td className="px-3 py-2 font-medium text-slate-900">
                              {settlement.payment_method_name}
                              {settlement.notes ? (
                                <div className="mt-1 text-xs font-normal text-slate-500">
                                  {settlement.notes}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {formatReportUserName(
                                settlement.created_by.first_name,
                                settlement.created_by.last_name,
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900">
                              {formatReportMoney(settlement.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </OrderDetailsSection>

            <OrderDetailsSection
              title="Discounts"
              subtitle={`${details.discounts.length.toLocaleString()} discount records on this order.`}
            >
              {details.discounts.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                  No discounts were applied to this order.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="max-h-[14rem] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-app-bg">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                          <th className="px-3 py-2">Added</th>
                          <th className="px-3 py-2">Reason</th>
                          <th className="px-3 py-2">User</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.discounts.map((discount) => (
                          <tr
                            key={discount.id}
                            className="border-t border-slate-100 align-top"
                          >
                            <td className="px-3 py-2 text-slate-700">
                              <div>{formatDetailDate(discount.created_at)}</div>
                              <div className="text-xs text-slate-500">
                                {formatClock(discount.created_at)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {discount.notes || 'No reason captured'}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {formatReportUserName(
                                discount.added_by.first_name,
                                discount.added_by.last_name,
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900">
                              {formatReportMoney(discount.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </OrderDetailsSection>
          </div>
        </div>
      )}
    </Modal>
  );
};
